/**
 * Web Worker for XR depth sorting
 * Adapted from splatxr.js
 */

import type { XRWorkerMessage } from '@/types/xr'

export function createXRWorkerCode(): string {
  return `
    let lastProj;
    let positions;
    let viewProj;
    let vertexCount;
    let lastVertexCount;
    let texture;

    self.onmessage = (e) => {
      if (e.data.view) {
        viewProj = e.data.view;
        throttledSort();
      } else if (e.data.texture) {
        texture = e.data.texture;
        vertexCount = Math.floor((texture.byteLength - e.data.remaining) / 4 / 16);
        if (vertexCount < 0) return;
        positions = new Float32Array(vertexCount * 3);
        for (let i = 0; i < vertexCount; i++) {
          positions[3 * i + 0] = texture[16 * i + 0];
          positions[3 * i + 1] = texture[16 * i + 1];
          positions[3 * i + 2] = texture[16 * i + 2];
        }
        throttledSort();
      }
    };

    let sortTimeout;
    function throttledSort() {
      if (!sortTimeout)
        sortTimeout = setTimeout(() => {
          runSort();
          sortTimeout = null;
        }, 0);
    }

    function runSort() {
      if (vertexCount < 0) return;
      if (!viewProj) return;
      if (lastProj && lastVertexCount === vertexCount) {
        let dist = Math.hypot(...[2, 6, 10].map((k) => lastProj[k] - viewProj[k]));
        if (dist < 0.01) return;
      }
      lastVertexCount = vertexCount;
      lastProj = viewProj;

      console.time("sort");
      let maxDepth = -Infinity;
      let minDepth = Infinity;
      let sizeList = new Int32Array(vertexCount);
      for (let i = 0; i < vertexCount; i++) {
        let depth =
          ((viewProj[2] * positions[3 * i + 0] + viewProj[6] * positions[3 * i + 1] + viewProj[10] * positions[3 * i + 2]) * 4096) | 0;
        sizeList[i] = depth;
        if (depth > maxDepth) maxDepth = depth;
        if (depth < minDepth) minDepth = depth;
      }
      // This is a 16 bit single-pass counting sort
      let depthInv = (256 * 256) / (maxDepth - minDepth);
      let counts0 = new Uint32Array(256 * 256);
      for (let i = 0; i < vertexCount; i++) {
        sizeList[i] = ((sizeList[i] - minDepth) * depthInv) | 0;
        counts0[sizeList[i]]++;
      }
      let starts0 = new Uint32Array(256 * 256);
      for (let i = 1; i < 256 * 256; i++) starts0[i] = starts0[i - 1] + counts0[i - 1];
      let depthIndex = new Uint32Array(vertexCount);
      for (let i = 0; i < vertexCount; i++) depthIndex[starts0[sizeList[i]]++] = i;
      console.timeEnd("sort");

      self.postMessage({ depthIndex, viewProj, vertexCount }, [depthIndex.buffer]);
    }
  `
}

/**
 * Create XR depth sorting worker
 */
export function createXRWorker(): Worker {
  const workerCode = createXRWorkerCode()
  const blob = new Blob([workerCode], { type: 'application/javascript' })
  const workerUrl = URL.createObjectURL(blob)
  return new Worker(workerUrl)
}

/**
 * XR Worker Manager class
 */
export class XRWorkerManager {
  private worker: Worker | null = null
  private onMessage: ((data: XRWorkerMessage) => void) | null = null

  constructor() {
    this.createWorker()
  }

  private createWorker() {
    this.worker = createXRWorker()
    this.worker.onmessage = (e) => {
      if (this.onMessage) {
        this.onMessage(e.data)
      }
    }
  }

  setMessageHandler(handler: (data: XRWorkerMessage) => void) {
    this.onMessage = handler
  }

  postMessage(data: XRWorkerMessage) {
    if (this.worker) {
      this.worker.postMessage(data)
    }
  }

  terminate() {
    if (this.worker) {
      this.worker.terminate()
      this.worker = null
    }
  }

  // Send view matrix for sorting
  updateView(viewProj: Float32Array) {
    this.postMessage({ view: viewProj })
  }

  // Send texture data for sorting
  updateTexture(texture: Float32Array, remaining: number) {
    this.postMessage({ texture, remaining })
  }
}